import React from 'react'
import { Alert, List, Stack, Textarea } from '@mantine/core'

import { ConditionalScriptBlock } from '@/components/Event/CrForm/components/ConditionalScriptBlock'
import { StateScriptBlock } from '@/components/Event/CrForm/components/StateScriptBlock'
import { TimestampsControl } from '@/components/Event/CrForm/components/TimestampsControl'
import {
  CrFormHandle,
  CrFormStep,
  witnessStepKey,
} from '@/components/Event/CrForm/CrFormStep'
import {
  getScriptValues,
  getStateRecordScript,
} from '@/components/Event/CrForm/stateRecordScripts'
import { Step } from '@/components/Event/CrForm/Step'
import { useFormattedCurrentDateParts } from '@/components/Event/NewScript/hooks/useFormattedCurrentDateParts'
import { Event } from '@/types'
import { getEventTimezone } from '@/utils/time'

// Questioning section (per witness).
export const QuestioningStep: React.FC<{
  event: Event
  form: CrFormHandle
  witnessIndex: number
}> = ({ event, form, witnessIndex }) => {
  const dt = useFormattedCurrentDateParts(getEventTimezone(event))
  const prefix = `events_feedbacks.${witnessIndex}`
  const witnessFeedback = form.values.events_feedbacks?.[witnessIndex]
  const announceRecord = form.values.sa?.announce_record ?? null
  const stateScript = getStateRecordScript(event)

  return (
    <Step
      id={witnessStepKey(CrFormStep.QUESTIONING, witnessIndex)}
      title="Questioning"
      form={form}
      nextStepId={witnessStepKey(CrFormStep.CLOSING, witnessIndex)}
    >
      <Stack>
        <Alert color="yellow" title="Reminders">
          <List size="sm" spacing={4}>
            <List.Item>Keep the witness spotlighted.</List.Item>
            <List.Item>Continue calling the record on each break.</List.Item>
            <List.Item>Log timestamps as you go.</List.Item>
          </List>
        </Alert>

        {stateScript ? (
          <StateScriptBlock
            heading="Going off and on the record"
            lines={stateScript.questioning}
            values={getScriptValues(event, null, dt)}
          />
        ) : (
          <>
            <ConditionalScriptBlock
              heading="Going Off the Record"
              announceRecord={announceRecord}
            >
              The time is now {dt.formattedTimeWithTimezone} and we are off the
              record.
            </ConditionalScriptBlock>
            <ConditionalScriptBlock
              heading="Going On the Record"
              announceRecord={announceRecord}
            >
              The time is now {dt.formattedTimeWithTimezone} and we are on the
              record.
            </ConditionalScriptBlock>
          </>
        )}

        <TimestampsControl
          event={event}
          form={form}
          witnessIndex={witnessIndex}
        />

        <Textarea
          label="Spellings"
          description="Capture name and term spellings confirmed on the record."
          minRows={3}
          autosize
          {...form.getInputProps(`${prefix}.spellings`)}
          value={witnessFeedback?.spellings ?? ''}
        />
        <Textarea
          label="General notes"
          minRows={3}
          autosize
          {...form.getInputProps(`${prefix}.additional_notes`)}
          value={witnessFeedback?.additional_notes ?? ''}
        />
      </Stack>
    </Step>
  )
}
